import { toFloatInt } from 'helpers/NumberFormats';
import type { ProductList } from 'types/checkout';

type PlaceOrderSourceItem = Pick<ProductList, 'asin' | 'quantity' | 'summedItemTotal'> &
  Partial<Pick<ProductList, 'productId' | 'stockId' | 'styleId'>>;
type PlaceOrderGroup = Pick<ProductList, 'asin'> & {
  productId: number | undefined;
  stockId: number | undefined;
  styleId: number | undefined;
  itemCount: number;
  price: number;
};

interface SerializePlaceOrderItemsOptions {
  includeStockId?: boolean;
  priceType?: 'lineTotal' | 'unit';
}

const toProductId = (value?: string): number | undefined => {
  const id = value === undefined ? undefined : toFloatInt(value);
  return typeof id === 'number' && Number.isInteger(id) && id >= 0 && id <= 0xffffffff ? id : undefined;
};

/**
 * Groups purchase lines by product and style for Amethyst order and checkout events.
 * Keeps lines separate when either catalog identifier is missing or invalid.
 * summedItemTotal already includes the quantity and item discounts; do not multiply it again.
 * Orders use line totals by default; checkout views use weighted unit prices and retain the first stockId.
 */
export const serializePlaceOrderItems = (
  productList: PlaceOrderSourceItem[] = [],
  { includeStockId = false, priceType = 'lineTotal' }: SerializePlaceOrderItemsOptions = {}
) => {
  const groups = new Map<string, PlaceOrderGroup>();

  productList.forEach(({ asin, productId, stockId, styleId, quantity, summedItemTotal }, index) => {
    const numericProductId = toProductId(productId);
    const numericStyleId = toProductId(styleId);
    const key = numericProductId !== undefined && numericStyleId !== undefined ? `product:${numericProductId}-${numericStyleId}` : `line:${index}`;
    const group = groups.get(key);

    if (group) {
      group.itemCount += quantity;
      group.price += summedItemTotal;
    } else {
      groups.set(key, {
        asin,
        productId: numericProductId,
        stockId: stockId === undefined ? undefined : toFloatInt(stockId),
        styleId: numericStyleId,
        itemCount: quantity,
        price: summedItemTotal
      });
    }
  });

  return [...groups.values()].map(({ asin, productId, stockId, styleId, itemCount, price }) => {
    // Round to integer cents before dividing so half-cent unit prices round correctly.
    const lineTotalCents = Math.round(price * 100);
    const priceCents = priceType === 'unit' && itemCount > 0 ? Math.round(lineTotalCents / itemCount) : lineTotalCents;

    return {
      asin,
      productId,
      ...(includeStockId && { stockId }),
      styleId,
      supplementalData: {
        itemCount,
        price: priceCents / 100
      }
    };
  });
};
